import React, { useState, useCallback, useEffect, useRef } from "react";
import "react-day-picker/style.css";
import { DayPicker } from "react-day-picker";
import { ptBR } from "react-day-picker/locale";
import styles from "./defaulting.module.css";
import HeaderTitle from "../../../components/header/headerTitle/headerTitle";
import Button from "../../../components/button/button";
import TriangleExclamationIcon from "../../../assets/icons/triangle-exclamation-blue.svg";
import SearchIcon from "../../../assets/icons/search-icon.svg";
import FileExportIcon from "../../../assets/icons/file-export.svg";
import BarsFilterIcon from "../../../assets/icons/bars-filter.svg";
import CalendarIcon from "../../../assets/icons/calendar.svg";
import clsx from "clsx";
import { defaultingStudents } from "../../../services/prediction";
import { insightsFilters } from "../../../services/insights";
import { useInstitution } from "../../../context/InstitutionContext";
import { toast } from "react-toastify";
import loadingGif from "../../../assets/loading.gif";
import useOutsideAlerter from "../../../utils/clickOutside";
import api from "../../../http/api";
import FilterSelect from "../../../components/predictive/filterSelect/filterSelect";
import BookIcon from '../../../assets/icons/book.svg';
import DiscIcon from '../../../assets/icons/discipline.svg';
import CourseIcon from '../../../assets/icons/course.svg';
import LocaleIcon from '../../../assets/icons/locale.svg';
import { getDateRange, saveDateRange } from "../../../utils/localStorage";

const Defaulting = () => {
  const { institution } = useInstitution();
  const calendarRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [defaultingData, setDefaultingData] = useState(null);
  const [defaultingApiResponse, setDefaultingApiResponse] = useState(null);
  const [defaultingLoading, setDefaultingLoading] = useState(false);
  const [exportingDefaulting, setExportingDefaulting] = useState(false);

  // Estados para paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState(null);

  // Estados para filtros de data
  const [defaultingDateRange, setDefaultingDateRange] = useState(() => {
    return getDateRange() || { from: new Date(new Date().getFullYear(), 0, 1), to: new Date() };
  });
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [suggestionRange, setSuggestionRange] = useState("");
  const [dateRangeString, setDateRangeString] = useState("");

  // Estados para filtros
  const [knowledgeAreas, setKnowledgeAreas] = useState([]);
  const [selectedKnowledgeArea, setSelectedKnowledgeArea] = useState("");
  const [knowledgeAreasLoading, setKnowledgeAreasLoading] = useState(false);
  const [knowledgeAreasFetched, setKnowledgeAreasFetched] = useState(false);

  const [disciplines, setDisciplines] = useState([]);
  const [selectedDiscipline, setSelectedDiscipline] = useState("");
  const [disciplinesLoading, setDisciplinesLoading] = useState(false);
  const [disciplinesFetched, setdisciplinesFetched] = useState(false);

  const [courseTypes, setCourseTypes] = useState([]);
  const [selectedCourseType, setSelectedCourseType] = useState("");
  const [courseTypesLoading, setCourseTypesLoading] = useState(false);
  const [courseTypesFetched, setcourseTypesFetched] = useState(false);

  const [localities, setLocalities] = useState([]);
  const [selectedLocality, setSelectedLocality] = useState("");
  const [localitiesLoading, setLocalitiesLoading] = useState(false);
  const [localitiesFetched, setlocalitiesFetched] = useState(false);

  useOutsideAlerter(calendarRef, () => setIsCalendarOpen(false));

  // Persistir data no localStorage para compartilhar entre telas
  useEffect(() => {
    if (defaultingDateRange?.from && defaultingDateRange?.to) {
      saveDateRange(defaultingDateRange);
    }
  }, [defaultingDateRange]);

  const formatCurrency = (value) => {
    // Dividir por 100 pois o valor vem em centavos
    const valueInReais = Number(value) / 100;
    return valueInReais.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const formatDate = (date) => {
    if (!date) return '';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const debounceTimerRef = useRef(null);

  const fetchDefaultingData = useCallback((filters = {}) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(async () => {
      try {
        setDefaultingLoading(true);

        const formatDateForAPI = (date) => {
          if (!date) return null;
          const day = date.getDate().toString().padStart(2, '0');
          const month = (date.getMonth() + 1).toString().padStart(2, '0');
          const year = date.getFullYear();
          return `${day}-${month}-${year}`;
        };

        const startDate = formatDateForAPI(defaultingDateRange.from);
        const endDate = formatDateForAPI(defaultingDateRange.to);

        const params = {
          institutionId: institution.id,
          startDate: startDate,
          endDate: endDate,
          knowledgeArea: filters.knowledgeArea || selectedKnowledgeArea,
          discipline: filters.discipline !== undefined ? filters.discipline : selectedDiscipline,
          courseTypeId: filters.courseTypeId !== undefined ? filters.courseTypeId : selectedCourseType,
          localityId: filters.localityId !== undefined ? filters.localityId : selectedLocality
        };

        const response = await defaultingStudents(params);

        setDefaultingApiResponse(response);
        setDefaultingData(response.defaultingStudents?.students || []);
      } catch (error) {
        console.error("Erro ao buscar alunos inadimplentes:", error);
        toast.error("Erro ao buscar dados de alunos inadimplentes");
        setDefaultingData([]);
      } finally {
        setDefaultingLoading(false);
      }
    }, 500);
  }, [institution.id, defaultingDateRange, selectedKnowledgeArea, selectedDiscipline, selectedCourseType, selectedLocality]);

  // Função auxiliar para verificar se um valor deve ser tratado como nulo
  const isNullValue = (value) => {
    return value == null || value === '' || (typeof value === 'string' && value.toUpperCase() === 'NULL');
  };

  const processedDefaultingData = defaultingData ? defaultingData.map((item) => ({
    id: item.studentId || '-',
    studentId: item.studentId || '-',
    city: !isNullValue(item.city) ? item.city : '-',
    state: !isNullValue(item.state) ? item.state : '-',
    neighborhood: !isNullValue(item.neighborhood) ? item.neighborhood : '-',
    birthDate: item.birthDate ? new Date(item.birthDate).toLocaleDateString('pt-BR') : '-',
    birthDateRaw: item.birthDate || null,
    nationality: !isNullValue(item.nationality) ? item.nationality : '-',
    gender: item.gender === 'M' ? 'Masculino' : item.gender === 'F' ? 'Feminino' : !isNullValue(item.gender) ? item.gender : '-',
    className: !isNullValue(item.className) ? item.className : '-',
    totalInstallments: item.totalInstallments != null ? item.totalInstallments : '-',
    overdueInstallments: item.overdueInstallments != null ? item.overdueInstallments : '-',
    overdueValue: item.overdueValue != null ? item.overdueValue : '-',
    avgFinalGrade: item.avgFinalGrade != null ? Number(item.avgFinalGrade).toFixed(2) : '-',
    avgIntermediateGrade: item.avgIntermediateGrade != null ? Number(item.avgIntermediateGrade).toFixed(2) : '-',
    avgFinalExamGrade: item.avgFinalExamGrade != null ? Number(item.avgFinalExamGrade).toFixed(2) : '-',
    avgFrequency: item.avgFrequency != null ? Number(item.avgFrequency).toFixed(2) : '-'
  })) : [];

  // Colunas da tabela; birthDate ordena pelo valor cru (a versão exibida é dd/mm/aaaa).
  const sortableColumns = [
    { label: "ID do Aluno", key: "studentId", center: true },
    { label: "Curso", key: "className" },
    { label: "Cidade", key: "city", center: true },
    { label: "Estado", key: "state", center: true },
    { label: "Bairro", key: "neighborhood" },
    { label: "Data de Nascimento", key: "birthDateRaw", center: true },
    { label: "Gênero", key: "gender" },
    { label: "Valor em Atraso", key: "overdueValue", center: true },
    { label: "Nota Final Média", key: "avgFinalGrade", center: true },
    { label: "Frequência Média", key: "avgFrequency" },
  ];

  // Ciclo por clique: asc → desc → ordem padrão (maior valor em atraso primeiro).
  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return prev.dir === "asc" ? { key, dir: "desc" } : null;
      }
      return { key, dir: "asc" };
    });
    setCurrentPage(1);
  };

  // Filtrar e ordenar tabela de inadimplentes
  const filteredDefaultingData = processedDefaultingData ? processedDefaultingData.filter(student => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      (student.studentId !== '-' && student.studentId.toString().includes(searchLower)) ||
      (student.city !== '-' && student.city.toLowerCase().includes(searchLower)) ||
      (student.state !== '-' && student.state.toLowerCase().includes(searchLower)) ||
      (student.neighborhood !== '-' && student.neighborhood.toLowerCase().includes(searchLower)) ||
      (student.className !== '-' && student.className.toLowerCase().includes(searchLower))
    );
  }).sort((a, b) => {
    if (!sortConfig) {
      // Ordem padrão: maior valor em atraso primeiro ("-" conta como 0).
      const valueA = a.overdueValue === '-' ? 0 : a.overdueValue;
      const valueB = b.overdueValue === '-' ? 0 : b.overdueValue;
      return valueB - valueA;
    }
    const va = a[sortConfig.key];
    const vb = b[sortConfig.key];
    const nullA = va == null || va === '-';
    const nullB = vb == null || vb === '-';
    if (nullA && nullB) {
      return 0;
    }
    if (nullA) {
      return 1;
    }
    if (nullB) {
      return -1;
    }
    const na = Number(va);
    const nb = Number(vb);
    const cmp = !Number.isNaN(na) && !Number.isNaN(nb)
      ? na - nb
      : String(va).localeCompare(String(vb));
    return sortConfig.dir === "asc" ? cmp : -cmp;
  }) : [];

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset para primeira página ao buscar
  };

  // Obter totalCount da API ou usar dados filtrados como fallback
  const totalCount = defaultingApiResponse?.defaultingStudents?.totalCount || filteredDefaultingData.length;

  // Calcular dados paginados
  const totalPages = Math.ceil(filteredDefaultingData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTableData = filteredDefaultingData.slice(startIndex, endIndex);

  const handlePageClick = (page) => {
    setCurrentPage(page);
  };


  const selectManualRange = () => {
    setSuggestionRange("");
    setCurrentPage(1); // Reset para primeira página ao filtrar
    fetchDefaultingData();
  };

  const selectSuggestionRange = (suggestionRange) => {
    setSuggestionRange(suggestionRange);
    setCurrentPage(1); // Reset para primeira página ao filtrar
    const currentYear = new Date().getFullYear();

    let selectedRange;
    switch (suggestionRange) {
      case "today":
        selectedRange = {
          from: new Date(),
          to: new Date()
        };
        break;

      case "last_7":
        selectedRange = {
          from: new Date(new Date().setDate(new Date().getDate() - 7)),
          to: new Date()
        };
        break;

      case "last_30":
        const last_30_now = new Date();
        selectedRange = {
          from: new Date(last_30_now.getFullYear(), last_30_now.getMonth(), 1),
          to: new Date(last_30_now.getFullYear(), last_30_now.getMonth() + 1, 0)
        };
        break;

      case "first_semester":
        selectedRange = {
          from: new Date(`${currentYear}-01-01T00:00:00`),
          to: new Date(`${currentYear}-06-30T23:59:59`)
        };
        break;

      case "second_semester":
        selectedRange = {
          from: new Date(`${currentYear}-07-01T00:00:00`),
          to: new Date(`${currentYear}-12-31T23:59:59`)
        };
        break;

      case "last_year":
        const now = new Date();
        selectedRange = {
          from: new Date(now.getFullYear() - 1, now.getMonth(), 1),
          to: new Date(now.getFullYear(), now.getMonth(), 0)
        };
        break;

      case "last_5_years":
        selectedRange = {
          from: new Date(new Date().setFullYear(new Date().getFullYear() - 5)),
          to: new Date()
        };
        break;

      case "current_year":
        selectedRange = {
          from: new Date(currentYear, 0, 1),
          to: new Date()
        };
        break;

      default:
        selectedRange = {
          from: new Date(new Date().setMonth(new Date().getMonth() - 3)),
          to: new Date(new Date().setMonth(new Date().getMonth() - 0))
        };
        break;
    }

    setDefaultingDateRange(selectedRange);
    fetchDefaultingData();
  };

  const handleClearSelection = () => {
    setSuggestionRange("");
    setDefaultingDateRange({
      from: new Date(),
      to: new Date()
    });
  };

  // Funções de busca dos filtros
  const fetchKnowledgeAreas = async (courseTypeFilter = null) => {
    if (knowledgeAreas.length > 0 && !courseTypeFilter) return;
    try {
      setKnowledgeAreasLoading(true);
      
      const params = {
        institutionId: institution.id,
        predictiveFilter: "knowledgearea"
      };
      
      // Se courseType foi fornecido, adiciona como filterClass
      if (courseTypeFilter) {
        params.filterClass = String(courseTypeFilter);
        console.log("fetchKnowledgeAreas - filterClass:", params.filterClass);
      }
      
      const response = await insightsFilters(params);

      setKnowledgeAreas(response.filterValues || []);
      setKnowledgeAreasFetched(true);
    } catch (error) {
      console.log("Erro ao buscar áreas de conhecimento", error);
    } finally {
      setKnowledgeAreasLoading(false);
    }
  };

  const fetchDisciplines = async () => {
    if (!selectedKnowledgeArea && !selectedCourseType) return;
    if (disciplines.length > 0) return;

    try {
      setDisciplinesLoading(true);
      
      const params = {
        institutionId: institution.id,
        predictiveFilter: "filterclass",
      };
      
      // Adicionar filtros opcionais se disponíveis
      if (selectedKnowledgeArea) {
        params.KnowledgeAreaFilter = String(selectedKnowledgeArea);
      }
      if (selectedCourseType) {
        params.filterClass = String(selectedCourseType);
      }
      
      const response = await insightsFilters(params);

      setDisciplines(response.filterValues || []);
      setdisciplinesFetched(true);
    } catch (error) {
      console.log("Erro ao buscar disciplinas", error);
    } finally {
      setDisciplinesLoading(false);
    }
  };

  const fetchCourseTypes = async () => {
    if (courseTypes.length > 0) return;

    try {
      setCourseTypesLoading(true);
      
      const params = {
        institutionId: institution.id,
        predictiveFilter: "filterdegree",
      };
      
      // Adicionar filtros opcionais se disponíveis
      if (selectedKnowledgeArea) {
        params.KnowledgeAreaFilter = String(selectedKnowledgeArea);
      }
      if (selectedDiscipline) {
        params.filterClass = String(selectedDiscipline);
      }
      
      const response = await insightsFilters(params);

      setCourseTypes(response.filterValues || []);
      setcourseTypesFetched(true);
    } catch (error) {
      console.log("Erro ao buscar tipos de curso", error);
    } finally {
      setCourseTypesLoading(false);
    }
  };

  const fetchLocalities = async () => {
    if (!selectedKnowledgeArea && !selectedDiscipline && !selectedCourseType) return;
    if (localities.length > 0) return;

    try {
      setLocalitiesLoading(true);
      
      const params = {
        institutionId: institution.id,
        predictiveFilter: "filterlocation",
      };
      
      // Adicionar filtros opcionais se disponíveis
      if (selectedKnowledgeArea) {
        params.KnowledgeAreaFilter = String(selectedKnowledgeArea);
      }
      if (selectedDiscipline) {
        params.filterClass = String(selectedDiscipline);
      }
      if (selectedCourseType) {
        params.courseTypeId = String(selectedCourseType);
      }
      
      const response = await insightsFilters(params);

      setLocalities(response.filterValues || []);
      setlocalitiesFetched(true);
    } catch (error) {
      console.log("Erro ao buscar localidades", error);
    } finally {
      setLocalitiesLoading(false);
    }
  };

  // Handlers de mudança dos filtros
  const handleKnowledgeAreaChange = (e) => {
    const newKnowledgeArea = e.target.value;
    console.log("handleKnowledgeAreaChange - Novo valor:", newKnowledgeArea);

    setSelectedKnowledgeArea(newKnowledgeArea);
    setSelectedDiscipline('');
    setSelectedLocality('');

    setDisciplines([]);
    setLocalities([]);

    setdisciplinesFetched(false);
    setlocalitiesFetched(false);

    setCurrentPage(1);
    fetchDefaultingData({ 
      knowledgeArea: newKnowledgeArea,
      courseTypeId: selectedCourseType 
    });
  };

  const handleDisciplineChange = (e) => {
    const newDiscipline = e.target.value;
    console.log("handleDisciplineChange - Novo valor:", newDiscipline);

    setSelectedDiscipline(newDiscipline);
    setSelectedLocality('');

    setLocalities([]);

    setlocalitiesFetched(false);

    setCurrentPage(1);
    fetchDefaultingData({
      knowledgeArea: selectedKnowledgeArea,
      discipline: newDiscipline,
      courseTypeId: selectedCourseType,
      localityId: ''
    });
  };

  const handleCourseTypeChange = async (e) => {
    const newCourseType = e.target.value;
    console.log("handleCourseTypeChange - Novo valor:", newCourseType);
    console.log("handleCourseTypeChange - Tipo:", typeof newCourseType);
    
    // Garantir que é uma string
    const courseTypeValue = String(newCourseType);
    
    setSelectedCourseType(courseTypeValue);
    
    // Limpar filtros dependentes
    setSelectedKnowledgeArea('');
    setSelectedDiscipline('');
    setSelectedLocality('');
    
    setKnowledgeAreas([]);
    setDisciplines([]);
    setLocalities([]);
    
    setKnowledgeAreasFetched(false);
    setdisciplinesFetched(false);
    setlocalitiesFetched(false);
    
    // Se um tipo de curso foi selecionado, buscar áreas de conhecimento com esse filtro
    if (courseTypeValue && courseTypeValue !== '') {
      console.log("Buscando áreas de conhecimento com courseType:", courseTypeValue);
      await fetchKnowledgeAreas(courseTypeValue);
    }
    
    setCurrentPage(1);
    fetchDefaultingData({
      knowledgeArea: '',
      discipline: '',
      courseTypeId: courseTypeValue,
      localityId: ''
    });
  };

  const handleLocalityChange = (e) => {
    setSelectedLocality(e.target.value);
    setCurrentPage(1);
    fetchDefaultingData({
      knowledgeArea: selectedKnowledgeArea,
      discipline: selectedDiscipline,
      courseTypeId: selectedCourseType,
      localityId: e.target.value
    });
  };

  const handleExportDefaultingCsv = async () => {
    if (!institution?.id) {
      toast.error("Instituição não encontrada");
      return;
    }

    if (!defaultingApiResponse?.defaultingStudents?.executedQuery) {
      toast.error("Query de exportação não disponível. Tente buscar os dados novamente.");
      return;
    }

    try {
      setExportingDefaulting(true);
      toast.info("Preparando exportação...");

      // Formatar datas para o formato esperado pela API (YYYY-MM-DD)
      const formatDateForAPI = (date) => {
        if (!date) return null;
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const startDate = formatDateForAPI(defaultingDateRange.from);
      const endDate = formatDateForAPI(defaultingDateRange.to);

      // Remover paginação da query para exportar todos os dados
      const exportQuery = defaultingApiResponse.defaultingStudents.executedQuery
        .replace(/OFFSET @Offset ROWS\s*FETCH NEXT @PageSize ROWS ONLY/gi, '');

      // Preparar parâmetros incluindo filtros selecionados
      const parameters = {
        InstitutionId: institution.id,
        StartDate: startDate,
        EndDate: endDate,
      };

      // Verificar se ao menos um filtro está selecionado
      const hasAnyFilter = selectedKnowledgeArea || selectedDiscipline || selectedCourseType || selectedLocality;

      // Se houver ao menos um filtro selecionado, enviar todos os filtros (com null para os não selecionados)
      if (hasAnyFilter) {
        parameters.UserKnowledgeArea = selectedKnowledgeArea || null;
        parameters.Discipline = selectedDiscipline || null;
        parameters.CourseType = selectedCourseType || null;
        parameters.CourseGrade = null; // Sempre null por enquanto
        parameters.Locality = selectedLocality || null;
      }

      const exportPayload = {
        executedQuery: exportQuery,
        parameters: parameters,
        headers: {
          StudentId: "ID do Aluno",
          City: "Cidade",
          State: "Estado",
          Neighborhood: "Bairro",
          BirthDate: "Data de Nascimento",
          Nationality: "Nacionalidade",
          Gender: "Gênero",
          TotalInstallments: "Total de Parcelas",
          OverdueInstallments: "Parcelas em Atraso",
          OverdueValue: "Valor em Atraso",
          AvgFinalGrade: "Média Nota Final",
          AvgIntermediateGrade: "Média Nota Intermediária",
          AvgFinalExamGrade: "Média Nota Prova Final",
          AvgFrequency: "Frequência Média"
        },
        fileName: `alunos_inadimplentes_${startDate}_a_${endDate}.csv`,
        includeHeaders: true,
        delimiter: ";",
        culture: "pt-BR"
      };

      const response = await api.client.post(
        'api/v1/CsvExport/export-from-query',
        exportPayload,
        {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json',
            institutionId: institution.id
          }
        }
      );

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', exportPayload.fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("CSV exportado com sucesso!");

    } catch (error) {
      console.error("Erro ao exportar CSV:", error);
      toast.error("Erro ao exportar dados. Tente novamente.");
    } finally {
      setExportingDefaulting(false);
    }
  };

  useEffect(() => {
    setDateRangeString(
      `${formatDate(defaultingDateRange?.from)} - ${formatDate(defaultingDateRange?.to)}`
    );
  }, [defaultingDateRange]);

  useEffect(() => {
    if (institution?.id) {
      fetchDefaultingData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institution?.id]);

  return (
    <div>
      <HeaderTitle title="Inadimplentes" icon={TriangleExclamationIcon}>
      </HeaderTitle>

      <div className={styles.filterWrapper}>
        <div className={styles.filterContainer}>
          <FilterSelect
            icon={CourseIcon}
            label="Tipo de Curso"
            value={selectedCourseType}
            loading={courseTypesLoading}
            options={courseTypes}
            onFocus={fetchCourseTypes}
            hasFetched={courseTypesFetched}
            onChange={handleCourseTypeChange}
          />
          <FilterSelect
            icon={BookIcon}
            label="Área de Conhecimento"
            value={selectedKnowledgeArea}
            loading={knowledgeAreasLoading}
            options={knowledgeAreas}
            onFocus={fetchKnowledgeAreas}
            hasFetched={knowledgeAreasFetched}
            onChange={handleKnowledgeAreaChange}
          />
          <FilterSelect
            icon={DiscIcon}
            label="Curso"
            value={selectedDiscipline}
            loading={disciplinesLoading}
            options={disciplines}
            onFocus={fetchDisciplines}
            hasFetched={disciplinesFetched}
            onChange={handleDisciplineChange}
          />
          <FilterSelect
            icon={LocaleIcon}
            label="Localidade"
            value={selectedLocality}
            loading={localitiesLoading}
            options={localities}
            onFocus={fetchLocalities}
            hasFetched={localitiesFetched}
            onChange={handleLocalityChange}
          />
        </div>

        <div className={styles.headerTitleActions}>
          <div className={styles.headerTitleActionsCalendar}>
            <Button
              icon={CalendarIcon}
              text={dateRangeString}
              size="normal"
              variation="outline"
              onClick={() => setIsCalendarOpen(!isCalendarOpen)}
            />

            {isCalendarOpen && (
              <div ref={calendarRef} className={styles.calenderContent}>
                <div className={styles.suggestions}>
                  <ul>
                    <li
                      onClick={() => !defaultingLoading && selectSuggestionRange("today")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "today"
                      })}
                      style={{ opacity: defaultingLoading ? 0.6 : 1, cursor: defaultingLoading ? 'not-allowed' : 'pointer' }}
                    >
                      Hoje
                    </li>
                    <li
                      onClick={() => !defaultingLoading && selectSuggestionRange("last_7")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_7"
                      })}
                      style={{ opacity: defaultingLoading ? 0.6 : 1, cursor: defaultingLoading ? 'not-allowed' : 'pointer' }}
                    >
                      Últimos 7 dias
                    </li>
                    <li
                      onClick={() => !defaultingLoading && selectSuggestionRange("last_30")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_30"
                      })}
                      style={{ opacity: defaultingLoading ? 0.6 : 1, cursor: defaultingLoading ? 'not-allowed' : 'pointer' }}
                    >
                      Último mês
                    </li>
                    <li
                      onClick={() => !defaultingLoading && selectSuggestionRange("first_semester")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "first_semester"
                      })}
                      style={{ opacity: defaultingLoading ? 0.6 : 1, cursor: defaultingLoading ? 'not-allowed' : 'pointer' }}
                    >
                      Primeiro Semestre
                    </li>
                    <li
                      onClick={() => !defaultingLoading && selectSuggestionRange("second_semester")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "second_semester"
                      })}
                      style={{ opacity: defaultingLoading ? 0.6 : 1, cursor: defaultingLoading ? 'not-allowed' : 'pointer' }}
                    >
                      Segundo Semestre
                    </li>
                    <li
                      onClick={() => !defaultingLoading && selectSuggestionRange("last_year")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_year"
                      })}
                      style={{ opacity: defaultingLoading ? 0.6 : 1, cursor: defaultingLoading ? 'not-allowed' : 'pointer' }}
                    >
                      Últimos 12 meses
                    </li>
                    <li
                      onClick={() => !defaultingLoading && selectSuggestionRange("current_year")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "current_year"
                      })}
                      style={{ opacity: defaultingLoading ? 0.6 : 1, cursor: defaultingLoading ? 'not-allowed' : 'pointer' }}
                    >
                      Ano Atual
                    </li>
                    <li
                      onClick={() => !defaultingLoading && selectSuggestionRange("last_5_years")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "last_5_years"
                      })}
                      style={{ opacity: defaultingLoading ? 0.6 : 1, cursor: defaultingLoading ? 'not-allowed' : 'pointer' }}
                    >
                      Últimos 5 Anos
                    </li>
                    <li
                      onClick={() => !defaultingLoading && selectSuggestionRange("manual")}
                      className={clsx(styles.suggestionsItem, {
                        [styles.active]: suggestionRange === "manual"
                      })}
                      style={{ opacity: defaultingLoading ? 0.6 : 1, cursor: defaultingLoading ? 'not-allowed' : 'pointer' }}
                    >
                      Personalizado
                    </li>
                  </ul>
                  <div>
                    <div
                      onClick={() => !defaultingLoading && handleClearSelection()}
                      className={clsx(styles.suggestionsItem, styles.active)}
                      style={{ opacity: defaultingLoading ? 0.6 : 1, cursor: defaultingLoading ? 'not-allowed' : 'pointer' }}
                    >
                      Limpar seleção
                    </div>

                    <div
                      onClick={() => !defaultingLoading && selectManualRange()}
                      className={clsx(
                        styles.suggestionsItem,
                        styles.active,
                        styles["btn-search-data"]
                      )}
                      style={{
                        opacity: defaultingLoading ? 0.8 : 1,
                        cursor: defaultingLoading ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {defaultingLoading ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <img
                            src={loadingGif}
                            alt="Carregando..."
                            style={{ width: "16px", height: "16px" }}
                          />
                          <span>Carregando...</span>
                        </div>
                      ) : (
                        <>
                          <img src={SearchIcon} alt="magnifying-glass" />
                          Buscar dados
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className={styles.dateRangeGroup}>
                  <div className={styles.datePickerWrapper}>
                    <label>Data de Início</label>
                    <DayPicker
                      mode="single"
                      selected={defaultingDateRange?.from}
                      onSelect={(from) => setDefaultingDateRange((prev) => ({ ...prev, from }))}
                      locale={ptBR}
                      captionLayout="dropdown"
                    />
                  </div>

                  <div className={styles.datePickerWrapper}>
                    <label>Data de Fim</label>
                    <DayPicker
                      mode="single"
                      selected={defaultingDateRange?.to}
                      onSelect={(to) => setDefaultingDateRange((prev) => ({ ...prev, to }))}
                      locale={ptBR}
                      captionLayout="dropdown"
                    />
                  </div>
                </div>

              </div>
            )}
          </div>

          <Button
            icon={BarsFilterIcon}
            text="Filtrar"
            size="normal"
            variation="outline"
            disabled={true}
          />
        </div>
      </div>

      <div className={styles.defaultingContent}>
        <div className={styles.tableContainer}>
          {/* Barra de busca e exportar dentro da tabela */}
          <div className={styles.tableHeader}>
            <div></div> {/* Espaço vazio para empurrar elementos para a direita */}

            <div className={styles.tableActions}>
              <div className={styles.searchWrapper}>
                <img src={SearchIcon} alt="Pesquisar" className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Pesquisar por ID, curso, cidade, estado ou bairro..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className={styles.searchInput}
                />
              </div>

              <Button
                icon={FileExportIcon}
                text={exportingDefaulting ? "Exportando..." : "Exportar CSV"}
                size="normal"
                variation="outline"
                loading={exportingDefaulting}
                disabled={exportingDefaulting}
                onClick={handleExportDefaultingCsv}
              />
            </div>
          </div>

          <table className={styles.defaultingTable}>
            <thead>
              <tr>
                {sortableColumns.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    style={{
                      cursor: "pointer",
                      userSelect: "none",
                      ...(col.center ? { textAlign: "center" } : {})
                    }}
                    title="Clique para ordenar"
                  >
                    {col.label}{" "}
                    {sortConfig?.key === col.key ? (
                      <span>{sortConfig.dir === "asc" ? "▲" : "▼"}</span>
                    ) : (
                      <span style={{ opacity: 0.35 }}>⇅</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {defaultingLoading ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: 'center', padding: '40px' }}>
                    <img src={loadingGif} alt="Carregando..." className={styles.loadingIcon} />
                    <p>Carregando dados de alunos inadimplentes...</p>
                  </td>
                </tr>
              ) : currentTableData.length > 0 ? (
                currentTableData.map((student) => (
                  <tr key={student.id}>
                    <td style={{ textAlign: 'center' }}>{student.studentId}</td>
                    <td>{student.className}</td>
                    <td style={{ textAlign: 'center' }}>{student.city}</td>
                    <td style={{ textAlign: 'center' }}>{student.state}</td>
                    <td>{student.neighborhood}</td>
                    <td style={{ textAlign: 'center' }}>{student.birthDate}</td>
                    <td>{student.gender}</td>
                    {/* <td style={{ textAlign: 'center' }}>{student.overdueInstallments}</td> */}
                    <td className={styles.valueCell}>
                      {student.overdueValue === '-' ? '-' : formatCurrency(student.overdueValue)}
                    </td>
                    <td style={{ textAlign: 'center' }}>{student.avgFinalGrade}</td>
                    <td>{student.avgFrequency === '-' ? '-' : `${student.avgFrequency}%`}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                    {defaultingData === null
                      ? "Use os filtros de data para buscar alunos inadimplentes"
                      : "Nenhum aluno inadimplente encontrado no período selecionado"
                    }
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Informação sobre total de registros */}
          {filteredDefaultingData && filteredDefaultingData.length > 0 && (
            <div className={styles.paginationInfo}>
              Total de {totalCount} aluno{totalCount !== 1 ? 's' : ''} inadimplente{totalCount !== 1 ? 's' : ''} encontrado{totalCount !== 1 ? 's' : ''}
            </div>
          )}

          {/* Paginação com apenas 3 páginas visíveis */}
          {filteredDefaultingData && filteredDefaultingData.length > 0 && totalPages > 1 && (
            <div className={styles.pagination}>
              {/* Botão "Anterior" */}
              {currentPage > 1 && (
                <button
                  className={styles.paginationButton}
                  onClick={() => handlePageClick(currentPage - 1)}
                >
                  Anterior
                </button>
              )}

              {/* Páginas visíveis */}
              {(() => {
                const pages = [];
                const startPage = Math.max(1, currentPage - 1);
                const endPage = Math.min(totalPages, startPage + 2);

                for (let i = startPage; i <= endPage; i++) {
                  pages.push(
                    <button
                      key={i}
                      className={i === currentPage ? styles.activePage : styles.paginationButton}
                      onClick={() => handlePageClick(i)}
                    >
                      {i}
                    </button>
                  );
                }
                return pages;
              })()}

              {/* Botão "Próximo" */}
              {currentPage < totalPages && (
                <button
                  className={styles.paginationButton}
                  onClick={() => handlePageClick(currentPage + 1)}
                >
                  Próximo
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Defaulting;
